import Highcharts = require("highcharts");
import * as d3 from 'd3';
import { TiChartSeriesComponent } from "../components/ti-chart-series.component";
import { TILogoComponent } from "../components/ti-logo.component";
import { TooltipComponent } from "../components/tooltip.component";
import { DateHelpers } from "../helpers/date-helpers";
import { FormatHelpers } from "../helpers/format-helpers";
import { IndicatorHelpers } from "../helpers/indicator-helper";
import { StringHelpers } from "../helpers/string-helpers";
import { TimeframeHelper } from "../helpers/timeframe-helpers";
import { TIDataPointToHighChartDataPointMapper } from "../mappers/data-point/ti-chart-point-to-highchart-data-point.mapper";
import { TIDataPointToHighChartVolumeDataPointMapper } from "../mappers/data-point/ti-chart-point-to-highchart-volume-data-point.mapper";
import { TIIndicatorPointToHighChartIndicatorPointMapper } from "../mappers/data-point/ti-indicator-point-to-highchart-indicator-point.mapper";
import { TIChartPointModel } from "../models/data-point/ti-chart-point.model";
import { TIIndicatorDataModel } from "../models/data-point/ti-indicator-data.model";
import { IndicatorOptionModel } from "../models/drop-down-list/indicator-option.model";
import { ChartType } from "../models/enum/chart-type.enum";
import { DrawingToolsType } from "../models/enum/drawing-tools-type.enum";
import { IndicatorType } from "../models/enum/indicator-type.enum";
import { Timeframe } from "../models/enum/timeframe.enum";
import { TIIndicatorDataToBBandsSeriesBuilder } from "./bbands-indicator-series.builder";
import { TIIndicatorDataToDCSeriesBuilder } from "./dc-indicator-series.builder";
import { TIIndicatorDataToKeltnerChannelSeriesBuilder } from "./keltner-channel-indicator-series.builder";
import { TIIndicatorDataToMacdSeriesBuilder } from "./macd-indicator-series.builder";
declare const window: any;

export class HighChartBuilder {
    public static readonly mainChartAreaName = 'MainChartArea';
    public mainSeriesClassName = 'main-chart-series';

    private tiChartSeries: TiChartSeriesComponent;

    private indicatorsToHideFromMainYAxis = [IndicatorType.AVWAP, IndicatorType.DistanceFromEMA, IndicatorType.MovingAverageEMA,
    IndicatorType.MovingAverageSMA, IndicatorType.BBands];

    private isDrawingToolRibbonActive = false;
    private isDrawingToolRibbonUpdate = false;
    private isDrawingToolStart = false;

    private chartSpacingTop = 46;

    constructor(tiChartSeries: TiChartSeriesComponent) {
        this.tiChartSeries = tiChartSeries;
        this.tiChartSeries.element.addEventListener('mousemove', () => {
            this.displayYAxisCurrentPrice(false);
        });
    }

    public getConfig(): Highcharts.Options {
        if (this.tiChartSeries.base.marketingWebChartMode) {
            this.chartSpacingTop = 36;

            if (this.tiChartSeries.base.pnpMode) {
                this.mainSeriesClassName = 'main-chart-pnp-series';

                if (this.tiChartSeries.element.clientHeight <= 230) {
                    this.chartSpacingTop = 24;
                }
            }
        } else {
            if (this.tiChartSeries.base.pnpMode) {
                this.chartSpacingTop = 0;
            }
        }

        const config = this.buildConfig();
        return config;
    }

    public drawingToolStartInActive() {
        setTimeout(() => {
            this.isDrawingToolStart = false;
        }, 150);
    }

    public drawingToolRibbonInActive() {
        setTimeout(() => {
            this.isDrawingToolRibbonActive = false;
        }, 150);
    }

    public drawingToolRibbonUpdate(executeCallback?: Function) {
        this.isDrawingToolRibbonUpdate = true;

        if (executeCallback) {
            executeCallback();
        }

        setTimeout(() => {
            this.isDrawingToolRibbonUpdate = false;
        }, 150);
    }

    public lineBetweenPoints = d3.line()
        .x((coordinates) => coordinates[0])
        .y((coordinates) => coordinates[1])
        .curve(d3.curveNatural);;

    private displayXAxisDateTimeTooltip(params: any) {
        const context = params as any;
        const wrapper = this.tiChartSeries.element.findFirst('.x-axis-label-wrapper');
        if (!wrapper || this.tiChartSeries.base.isPreview) {
            return;
        }

        const point = this.tiChartSeries.tiChartPoints.find((x: TIChartPointModel) => x.xValue == context.x);
        if (!point) {
            return;
        }

        const el = this.tiChartSeries.element.findFirst('.x-axis-label');
        const pElement = el?.querySelector('p');
        if (!el || !pElement) {
            return;
        }

        pElement.innerHTML = DateHelpers.format(TimeframeHelper.getDateTimeTooltipFormat(this.tiChartSeries.currentTimeframe), point.start);

        const xAxis = params.series.xAxis;
        const myWidth = el.clientWidth;
        const extraTop = this.tiChartSeries.hasAnyComparisonSymbols() ? 20 : 0;
        const xAxisTop = xAxis.height + extraTop;

        let left = context.plotX - myWidth / 2;
        let top = this.chartSpacingTop + xAxisTop;

        if (top < 2) {
            top = 2;
        }

        if (left < 2) {
            left = 2;
        }

        wrapper.style.top = top + 'px';
        wrapper.style.left = left + 'px';

        wrapper.classList.add('active');
    }

    private hideXAxisDateTimeTooltip() {
        const wrapper = this.tiChartSeries.element.findFirst('.x-axis-label-wrapper');
        if (!wrapper) {
            return;
        }

        wrapper.classList.remove('active');
    }

    private displayYAxisCurrentPrice(active: boolean) {
        if (this.tiChartSeries.chartStyleSettings?.isComparisonSymbolPercentageMode || this.tiChartSeries.base.isPreview) {
            this.hideYAxisCurrentPrice();
            return;
        }

        const crossHairYAxis = this.tiChartSeries.element.findFirst('.highchart-main-crosshair.y-axis');
        const wrapper = this.tiChartSeries.element.findFirst('.main-symbol-current-price');
        if (!wrapper || !crossHairYAxis) {
            return;
        }

        const yAxis: any = this.tiChartSeries.highStockChart?.yAxis ? this.tiChartSeries.highStockChart.yAxis[0] : null;
        if (!yAxis) {
            return;
        }

        const crossHairTop = crossHairYAxis.getBoundingClientRect();
        let currentValue = 0;
        let xAxisTop = 0;

        if (this.tiChartSeries.base.marketingWebChartMode || this.tiChartSeries.base.pnpMode) {
            const parentElementRect = this.tiChartSeries.element.getBoundingClientRect();
            xAxisTop = crossHairTop.y - parentElementRect.y;
            currentValue = yAxis.toValue(xAxisTop);
        } else {
            xAxisTop = crossHairTop.y;
            currentValue = yAxis.toValue(crossHairTop.y);
        }

        const pElement = wrapper.findFirst('p');

        let priceToString = '';
        if (currentValue < 1) {
            priceToString = FormatHelpers.numberToString(currentValue, 4);
        } else {
            priceToString = FormatHelpers.numberToString(currentValue);
        }

        pElement?.setInnerHtml(priceToString);

        const myTop = wrapper.clientHeight;
        const extraTop = this.tiChartSeries.hasAnyComparisonSymbols() ? 20 : 0;
        xAxisTop += extraTop;

        let top = xAxisTop - myTop / 2;

        if (top < 2) {
            top = 2;
        }

        wrapper.style.top = top + 'px';

        if (active)
            wrapper.removeClass('hidden');
    }

    private hideYAxisCurrentPrice() {
        const wrapper = this.tiChartSeries.element.findFirst('.main-symbol-current-price');
        if (!wrapper) {
            return;
        }

        wrapper.classList.add('hidden');
    }

    private setXAxisHeight() {
        const yAxis = this.tiChartSeries.highStockChart.yAxis[0] as any;
        const xAxis = this.tiChartSeries.highStockChart.xAxis[0] as any;
        if (!yAxis || !xAxis) {
            return;
        }

        if (yAxis.height != xAxis.height) {
            this.tiChartSeries.highStockChart.xAxis[0].update({
                height: yAxis.height
            });
        }
    }

    private setYAxisHeight() {
        const mainYAxisId = HighChartBuilder.mainChartAreaName + '-y-axis';
        const yAxis: any = this.tiChartSeries.highStockChart.get(mainYAxisId);
        const indicatorMainYAxisId = HighChartBuilder.mainChartAreaName + '-ind-y-axis';
        const indicatorMainYAxis: any = this.tiChartSeries.highStockChart.get(indicatorMainYAxisId);
        if (!yAxis || !indicatorMainYAxis) {
            return;
        }

        // console.log(' this.tiChartSeries.highStockChart',  this.tiChartSeries.highStockChart)
        // console.log('y axis height', yAxis.height)
        // console.log('y axis top', yAxis.top)
        // console.log('indicatorMainYAxis y axis height', indicatorMainYAxis.height)
        // console.log('indicatorMainYAxis y axis top', indicatorMainYAxis.top)
        if (yAxis.height != indicatorMainYAxis.height) {
            const logoComponent = this.tiChartSeries.base.getComponentsByType(TILogoComponent)[0] as TILogoComponent;
            if (logoComponent) {
                logoComponent.setPanelStyle();
            }

            indicatorMainYAxis.update({
                height: yAxis.height
            });
        }

        if (yAxis.top != indicatorMainYAxis.top) {
            indicatorMainYAxis.update({
                top: yAxis.top
            });
        }
    }

    private setAxisExtraLine() {
        const chartStlye = this.tiChartSeries.chartStyleSettings;
        const xAxis = this.tiChartSeries.highStockChart.xAxis[0] as any;
        const highchartContainer = this.tiChartSeries.element.findFirst('.highcharts-container');
        const existXAxisSeperatorElement = highchartContainer?.findFirst('.axis-sep.x');
        const existYAxisSeperatorElement = highchartContainer?.findFirst('.axis-sep.y');
        if (!chartStlye || !xAxis || this.tiChartSeries.base.isPreview) {
            return;
        }

        existXAxisSeperatorElement?.remove();
        existYAxisSeperatorElement?.remove();

        const extraTop = this.tiChartSeries.hasAnyComparisonSymbols() ? 20 : 0;
        const xAxisTop = this.chartSpacingTop + xAxis.height + extraTop;

        const xAxisSeperatorElement = document.createElement('div');
        const yAxisSeperatorElement = document.createElement('div');

        xAxisSeperatorElement.className = 'axis-sep x';
        xAxisSeperatorElement.style.top = xAxisTop + 'px';

        yAxisSeperatorElement.className = 'axis-sep y';

        if (chartStlye.showSmartRiskLevel) {
            xAxisSeperatorElement.style.right = '100px';
            yAxisSeperatorElement.style.right = '100px';
        } else {
            xAxisSeperatorElement.style.right = '70px';
            yAxisSeperatorElement.style.right = '70px';
        }

        if (this.tiChartSeries.base.pnpMode) {
            xAxisSeperatorElement.style.right = '55px';
            yAxisSeperatorElement.style.right = '55px';

            const top = this.chartSpacingTop + 5;
            yAxisSeperatorElement.style.top = `${top}px`;
            yAxisSeperatorElement.style.height = `calc(100% - ${top}px)`;
        }

        highchartContainer?.insertAdjacentElement('beforeend', xAxisSeperatorElement);
        highchartContainer?.insertAdjacentElement('beforeend', yAxisSeperatorElement);
    }

    private buildConfig(): Highcharts.Options {
        const me = this;
        const chartStlye = me.tiChartSeries.chartStyleSettings;
        if (!chartStlye) {
            return {};
        }

        const drawingToolManager = me.tiChartSeries.base.drawingToolsManager;
        const allIndicatorOptions = me.tiChartSeries.getAllActiveIndicatorOptions();
        const hasAnyNonMainChartAreaIndicators = allIndicatorOptions.some((x: IndicatorOptionModel) => x.chartAreaName != HighChartBuilder.mainChartAreaName);

        let clickNumber = 0;
        let cuvedPointsForNewAnnotation = new Array();

        let chartSpacingRight = 94;
        if (chartStlye.showSmartRiskLevel) {
            chartSpacingRight = 124;
        }

        if (me.tiChartSeries.base.pnpMode) {
            chartSpacingRight = 56;

            if (!me.tiChartSeries.base.marketingWebChartMode) {
                chartSpacingRight = 68;
            }
        }

        let height: number | null = null;
        let chartSpacingBottom = hasAnyNonMainChartAreaIndicators ? -22 : 0;
        if (me.tiChartSeries.base.isPreview) {
            this.chartSpacingTop = 0;
            
            const statusElement = this.tiChartSeries.element.findFirst('.marketing-status-line');
            if (this.tiChartSeries.element.clientHeight <= 60) {
                this.chartSpacingTop = statusElement.clientHeight + 6;
            }
            else {
                this.chartSpacingTop = statusElement.clientHeight + 12;
            }


            height = this.tiChartSeries.element.clientHeight;
            chartSpacingRight = 0;
            chartSpacingBottom = 0;

            console.log(`height: ${height}, this.chartSpacingTop: ${this.chartSpacingTop}`)
        }

        const chartConfig: any = {
            plotOptions: {
                series: {
                    turboThreshold: 1000000,
                    animation: false,
                    point: {
                        events: {
                            remove: function () {
                                const point = this;
                                if (point && point.series && point.series.userOptions && point.series.userOptions.type == 'flags') {

                                    setTimeout(() => {
                                        console.log('point remove', this)
                                        drawingToolManager?.delete();
                                    }, 300);
                                }
                            },
                            update: function () {
                                const point = this;
                                if (point && point.series && point.series.userOptions && point.series.userOptions.type == 'flags') {

                                    setTimeout(() => {
                                        console.log('point update', this)
                                        drawingToolManager?.saveAnnotations();
                                    }, 300);
                                }
                            }
                        }
                    }
                }
            },
            accessibility: {
                keyboardNavigation: {
                    enabled: false
                }
            },
            chart: {
                animation: false,
                spacingTop: this.chartSpacingTop,
                spacingRight: chartSpacingRight,
                spacingBottom: chartSpacingBottom,
                spacingLeft: 0,
                styledMode: true,
                events: {
                    load: function () {
                        const highChartRoot = me.tiChartSeries.element.querySelector('.highcharts-root defs');
                        if (highChartRoot) {
                            const bgDefTags = me.tiChartSeries.highChartStyleBuilder.getTIChartBackgroundSVGDefs(chartStlye);
                            if (bgDefTags) {
                                highChartRoot.appendChild(bgDefTags);
                            }

                            const bgAreaDefTags = me.tiChartSeries.highChartStyleBuilder.getTIChartAreaBackgroundSVGDefs(chartStlye);
                            // console.log('bgAreaDefTags ', bgAreaDefTags, me.tiChartSeries.base.chartService)
                            if (bgAreaDefTags) {
                                highChartRoot.appendChild(bgAreaDefTags);
                            }
                        }

                        // console.log("Loaded", this);

                        // var highChart: any = Highcharts;
                        //var chart: any = me.tiChartSeries.highStockChart;
                        this.container.addEventListener("mousemove", (e: any) => {

                            // console.log("moving mouse " + clickNumber);
                            if (clickNumber == 2) {
                                if (cuvedPointsForNewAnnotation[0][1] >= e.chartY + 5) {
                                    cuvedPointsForNewAnnotation[1] = [(cuvedPointsForNewAnnotation[0][0] + e.chartX) / 2, e.chartY - 50];
                                }
                                else {
                                    cuvedPointsForNewAnnotation[1] = [(cuvedPointsForNewAnnotation[0][0] + e.chartX) / 2, e.chartY + 50];
                                }
                                cuvedPointsForNewAnnotation[2] = [e.chartX, e.chartY];

                                if (d3.select('#dtcl_path_Guide').empty()) {
                                    d3.select(".highcharts-root").append("path").datum(cuvedPointsForNewAnnotation)
                                        .attr("d", me.lineBetweenPoints)
                                        .attr("id", "dtcl_path_Guide")
                                        .attr("class", "dtcl_GuideLine2")
                                        .attr("fill", "none")
                                        .attr("stroke", "#ccc")
                                        .attr("stroke-width", "3");

                                }
                                else {
                                    d3.select('#dtcl_path_Guide').attr('d', me.lineBetweenPoints(cuvedPointsForNewAnnotation));
                                }
                            }
                        });

                        me.tiChartSeries.element.findFirst('.main-chart')?.addClass('active');

                        setTimeout(() => {
                            drawingToolManager?.sync();
                        }, 500);
                    },
                    click: (e) => {
                        const el = me.tiChartSeries.element.findFirst('.tooltip');
                        el?.removeClass('active');

                        switch (clickNumber) {
                            case 1:
                                cuvedPointsForNewAnnotation[0] = [e.chartX, e.chartY];
                                ++clickNumber;
                                break;

                            case 2:
                                cuvedPointsForNewAnnotation[2] = [e.chartX, e.chartY];
                                drawingToolManager?.drawCustomAnnotation(DrawingToolsType.CurvedLine, cuvedPointsForNewAnnotation);
                                cuvedPointsForNewAnnotation = Array();
                                d3.select('#dtcl_path_Guide').remove();
                                clickNumber = 0;
                                break;
                            default:
                                break;
                        }

                    },
                    redraw: () => {
                        me.setXAxisHeight();
                        me.setYAxisHeight();
                        me.setAxisExtraLine();
                        this.tiChartSeries.onRedraw();
                    },
                    afterAddSeries: function (e) {
                        if (e.series && e.series.userOptions && e.series.userOptions.type == 'flags' && !e.series.options.id) {
                            e.series.options.id = StringHelpers.newGuid();
                            drawingToolManager?.drawAnnotation(e.series);
                        } 
                    },
                }
            },
            title: {
                text: '',
            },
            xAxis: this.getXAxis(allIndicatorOptions),
            yAxis: this.getYAxis(allIndicatorOptions),
            series: this.getSeries(allIndicatorOptions),
            colors: ['#ff0000'],
            scrollbar: {
                enabled: false
            },
            rangeSelector: {
                enabled: false
            },
            credits: {
                enabled: false
            },
            navigator: {
                enabled: false
            },
            exporting: {
                enabled: false
            },
            tooltip: {
                formatter: (p: any) => {
                    return '';
                },
                style: {
                    position: 'absolute',
                    top: '0'
                },
                positioner: (labelWidth, labelHeight, point) => {
                    let plot = {
                        x: point.plotX + labelWidth / 2 + 20,
                        y: point.plotY + labelHeight / 2 - 50
                    };

                    if (plot.x + labelWidth > window.innerWidth) {
                        plot.x = plot.x - (labelWidth + 40);
                    }

                    return plot;
                }
            },
            stockTools: {
                gui: {
                    buttons: [
                        // 'curvedLineCustomAnnotation',
                        'lines',
                        'simpleShapes',
                        'crookedLines',
                        'verticalLabels',
                        'advanced',
                        'measure',
                        // 'indicators',
                        'flags'
                    ]
                },
            },
            navigation: {
                annotationsOptions: {
                    events: {
                        add: (highChartDrawingToolsEvent: any) => {
                            if (highChartDrawingToolsEvent.target?.options) {
                                highChartDrawingToolsEvent.target.options.id = StringHelpers.newGuid();
                            }

                            drawingToolManager?.drawAnnotation(highChartDrawingToolsEvent.target);
                            me.tiChartSeries.onAnnotationAdd(highChartDrawingToolsEvent, false);
                        },
                        afterUpdate: (highChartDrawingToolsEvent: any) => {
                            // if (highChartDrawingToolsEvent.target.options && !highChartDrawingToolsEvent.target.options.id) {
                            //     highChartDrawingToolsEvent.target.options.id = StringHelpers.newGuid();
                            // }

                            me.drawingToolRibbonUpdate(() => {
                                const afterDrawingToolCompleted = highChartDrawingToolsEvent && highChartDrawingToolsEvent.target && !highChartDrawingToolsEvent.target.isUpdating;
                                if (afterDrawingToolCompleted) {
                                    drawingToolManager?.saveAnnotations();
                                }

                                me.tiChartSeries.onAnnotationAfterUpdate(highChartDrawingToolsEvent);
                            });
                        },
                        remove: () => {
                            me.tiChartSeries.base.drawingToolsManager?.delete();
                        }
                    },
                    shapeOptions: {
                        fill: 'none',
                        stroke: 'black',
                    }
                },
                events: {
                    showPopup: (event: any) => {
                        me.isDrawingToolRibbonActive = true;
                        me.tiChartSeries.onPopupChange(true, event);
                    },
                    closePopup: (event: any) => {
                        me.tiChartSeries.onPopupChange(false, event);
                        me.drawingToolRibbonInActive();

                        const afterDrawingToolCompleted = event && event.target && !event.target.isUpdating;
                        if (afterDrawingToolCompleted) {
                            drawingToolManager?.saveAnnotations();
                        }
                    },
                    selectButton: () => {
                        me.isDrawingToolStart = true;
                    },
                    deselectButton: () => {
                        setTimeout(() => {
                            me.isDrawingToolStart = false;
                        }, 150);
                    }
                }
            }
        };

        const buttons = [
            // 'emojiCustom2Annotation',
            'simpleShapes',
            'crookedLines',
            'verticalLabels',
            'advanced',
            'measure',
            // 'indicators',
            'flags'
        ];

        if (chartStlye.emojiDrawingToolsEnabled) {
            buttons.unshift('emojiCustomAnnotation');
        }

        if (!chartStlye.curvedLineDisabled) {
            buttons.unshift('curvedLineCustomAnnotation');
        }

        buttons.unshift('lines');
        chartConfig.stockTools.gui = {
            buttons: buttons,

            definitions: {
                curvedLineCustomAnnotation: {
                    title: 'curved line',
                    className: 'highcharts-curved-line-custom-annotation'
                },
                emojiCustomAnnotation: {
                    title: 'emoji',
                    className: 'highcharts-emoji-custom-annotation'
                },
                emojiCustom2Annotation: {
                    title: 'emoji',
                    className: 'highcharts-emoji2-custom-annotation'
                },
            }
        };

        chartConfig.navigation.bindings = {
            curvedLineCustomAnnotation: {
                title: 'curved line',
                className: 'highcharts-curved-line-custom-annotation',
                init: function (e: any) {
                    // flagCurvedLilePlace = true;
                    clickNumber = 1;

                }
            },
            emojiCustomAnnotation: {
                title: 'emoji',
                className: 'highcharts-emoji-custom-annotation',
                init: function (e: any) {
                    console.log('emojiCustomAnnotation init')
                },
                start: function (e: any) {
                    console.log('emojiCustomAnnotation start')
                    e.emojiType = 1;
                    me.tiChartSeries.base.drawingToolsManager.drawCustomAnnotation(DrawingToolsType.Emoji, e);
                }
            },
            emojiCustom2Annotation: {
                title: 'emoji',
                className: 'highcharts-emoji2-custom-annotation',
                init: function (e: any) {
                    // console.log('emojiCustomAnnotation init')
                },
                start: function (e: any) {
                    // console.log('emojiCustomAnnotation start')
                    e.emojiType = 2;
                    me.tiChartSeries.base.drawingToolsManager.drawCustomAnnotation(DrawingToolsType.Emoji, e);
                    me.tiChartSeries.base.drawingToolsManager.removeRootDrawMode(false);
                }
            },
            labelAnnotation: {
                start: function (e: any) {
                    me.tiChartSeries.base.drawingToolsManager.drawCustomAnnotation(DrawingToolsType.NewTextBox, e);
                }
            }
        };

        if (height > 0) {
            console.log('marketing webchart yaxis height ', height)
            chartConfig.chart.height = height;
        }

        return chartConfig;
    }

    private getXAxis(allIndicators: Array<IndicatorOptionModel>): Array<Highcharts.XAxisOptions> {
        const mainXAxis = this.getMainChartXAxis();
        const result: Array<any> = [];
        const indicatorAxisAreas: Array<any> = [];
        for (const indicator of allIndicators) {
            if (!indicatorAxisAreas.some((x: string) => x == indicator.chartAreaName)) {
                indicatorAxisAreas.push(indicator.chartAreaName);
            }
        }
        const allIndicatorCount = indicatorAxisAreas.filter((x: string) => x != HighChartBuilder.mainChartAreaName).length;
        for (const indicator of allIndicators) {
            const indicatorConfig: any = IndicatorHelpers.getIndicatorHighChartConfig(indicator.value);
            if (indicatorConfig?.xAxis) {
                result.push(indicatorConfig.xAxis as any);
            }
        }

        if (allIndicatorCount == 1) {
            mainXAxis.height = '75%';
        } else if (allIndicatorCount > 1) {
            mainXAxis.height = '50%';
        }

        result.unshift(mainXAxis)
        return result;
    }

    private getMainChartXAxis(): Highcharts.XAxisOptions {
        const me = this;
        const xAxisConfigOption: Highcharts.XAxisOptions = {
            ordinal: true,
            type: "linear",
            crosshair: {
                className: 'highchart-main-crosshair',
                dashStyle: 'Dash',
                snap: false
            },
            panningEnabled: false,
            labels: {
                formatter: (p: any) => {
                    const point = this.tiChartSeries.tiChartPoints.find((x: TIChartPointModel) => x.xValue == p.value);

                    if (!point) {
                        return '';
                    }

                    if (this.tiChartSeries.highChartAxisBuilder.xValuesForDarkersGridlines.some((x: number) => x == point.xValue)) {
                        // setTimeout(() => {
                        //     p.tick.gridLine?.element?.classList.add('darker');
                        //     p.tick.label?.element?.classList.add('bold');
                        // }, 20);

                        p.tick.gridLine?.element?.classList.add('darker');
                        p.tick.label?.element?.classList.add('bold');
                    }

                    // console.log(p, this.tick.gridLine)
                    if (this.tiChartSeries.highChartAxisBuilder.xValuesForDateTime.some((x: number) => x == point.xValue)) {
                        if (!TimeframeHelper.timeframeIntraday(me.tiChartSeries.currentTimeframe)) {
                            if (me.tiChartSeries.currentTimeframe == Timeframe.Week) {
                                if (me.tiChartSeries.currentZoom >= -3 && me.tiChartSeries.currentZoom <= 1) {
                                    // console.log('zoom', this.tiChartSeries.currentZoom)
                                    return DateHelpers.format('m-d', point.start);
                                }
                            }
                            return DateHelpers.format('SM-yy', point.start);
                        }

                        let dateSeparator = DateHelpers.format('m-d', point.start);
                        //console.log('getMainChartXAxis, dateSeparator', dateSeparator)
                        //Display darker gridlines between days on intraday timeframes
                        // setTimeout(() => {
                        //     p.tick.gridLine?.element?.classList.add('darker');
                        // }, 200);

                        return dateSeparator;
                    } else if (this.tiChartSeries.highChartAxisBuilder.xValuesForDateTimeWithYear.some((x: number) => x == point.xValue)) {
                        return DateHelpers.format('m-d-yy', point.start);
                    } else if (this.tiChartSeries.highChartAxisBuilder.xValuesForHours.some((x: number) => x == point.xValue)) {
                        return DateHelpers.format('HH:MM', point.start);
                    }

                    return '';
                }
            },
            events: {
                setExtremes: (ev: any) => {
                    this.tiChartSeries.onExtremeChange(ev);
                }
            }
        };

        if (this.tiChartSeries.base.isPreview) {
            xAxisConfigOption.visible = false;
        }

        return xAxisConfigOption;
    }

    private getYAxis(allIndicators: Array<IndicatorOptionModel>): Array<Highcharts.YAxisOptions> {
        const mainYAxis = this.getMainChartYAxis();
        const result: Array<any> = [];
        const indicatorAxisAreas: Array<any> = [];
        const chartStyle = this.tiChartSeries.chartStyleSettings;
        for (const indicator of allIndicators) {
            if (!indicatorAxisAreas.some((x: string) => x == indicator.chartAreaName)) {
                indicatorAxisAreas.push(indicator.chartAreaName);
            }
        }
        const allIndicatorCount = indicatorAxisAreas.filter((x: string) => x != HighChartBuilder.mainChartAreaName).length;
        let indicatorCount = 0;
        const padding = 3;
        const addedIndicators: Array<any> = [];
        const addedIndicatorsIdForResizer: Array<any> = [];
        for (const indicator of allIndicators) {
            const indicatorConfig: any = IndicatorHelpers.getIndicatorHighChartConfig(indicator.value);
            const id = indicator.chartAreaName + '-y-axis';
            // if (indicator.chartAreaName == HighChartBuilder.mainChartAreaName || addedIndicators.some((x: string) => x == id)) {
            if ((indicator.chartAreaName == HighChartBuilder.mainChartAreaName && !this.indicatorsToHideFromMainYAxis.some((x: IndicatorType) => x == indicator.value)) || addedIndicators.some((x: string) => x == id)) {
                continue;
            }

            addedIndicators.push(id);

            if (indicatorConfig?.yAxis) {
                if (chartStyle?.showSmartRiskLevel) {
                    indicatorConfig.yAxis.labels.x = 112;
                } else {
                    indicatorConfig.yAxis.labels.x = 80;
                }

                if (this.tiChartSeries.base.pnpMode) {
                    indicatorConfig.yAxis.labels.x = 55;
                }

                if (indicator.chartAreaName != HighChartBuilder.mainChartAreaName) {
                    indicatorConfig.yAxis.resize = {
                        enabled: true
                    };

                    indicatorConfig.yAxis.title = {
                        align: 'high',
                        offset: 0,
                        margin: 0,
                        text: indicator.text,
                        useHTML: true,
                        reserveSpace: false,
                        style: {
                            left: 0,
                        }
                    };

                    indicatorConfig.yAxis.id = id;
                    indicatorConfig.yAxis.labels.style.color = this.tiChartSeries.chartStyleSettings?.selectedPriceTextColor.toHex() ?? '';

                    if (allIndicatorCount == 1) {
                        if (this.tiChartSeries.base.marketingWebChartMode) {
                            indicatorConfig.yAxis.height = '14%';
                            indicatorConfig.yAxis.top = '86%';
                        } else {
                            indicatorConfig.yAxis.height = '22%';
                            indicatorConfig.yAxis.top = '78%';
                        }
                    } else if (allIndicatorCount > 1) {
                        const scalePercentage = 50 / allIndicatorCount;
                        indicatorConfig.yAxis.height = (scalePercentage - padding) + '%';
                        indicatorConfig.yAxis.top = (53 + (scalePercentage * indicatorCount)) + '%';
                    }

                    indicatorCount++;
                    addedIndicatorsIdForResizer.push(id);
                } else if (this.indicatorsToHideFromMainYAxis.some((x: IndicatorType) => x == indicator.value)) {
                    indicatorConfig.yAxis.resize = {
                        enabled: false
                    };
                    indicatorConfig.yAxis.labels.enabled = false;
                    indicatorConfig.yAxis.id = indicator.chartAreaName + '-ind-y-axis';
                    if (allIndicatorCount == 1) {
                        indicatorConfig.yAxis.height = '75%';
                    } else if (allIndicatorCount > 1) {
                        indicatorConfig.yAxis.height = '50%';
                    } else {
                        indicatorConfig.yAxis.height = '100%';
                    }
                } else {
                    indicatorConfig.yAxis.id = indicator.chartAreaName + '-y-axis';
                }

                result.push(indicatorConfig.yAxis as any);
            }
        }

        if (allIndicatorCount == 1) {
            if (this.tiChartSeries.base.marketingWebChartMode) {
                mainYAxis.height = '82%';
            } else {
                mainYAxis.height = '75%';
            }
        } else if (allIndicatorCount > 1) {
            mainYAxis.height = '50%';
        }

        if (addedIndicatorsIdForResizer?.length) {
            mainYAxis.resize.controlledAxis = {
                next: [],
                prev: []
            };

            for (let i = 0; i < addedIndicatorsIdForResizer.length; i++) {
                const id = addedIndicatorsIdForResizer[i];
                if (i == 0) {
                    mainYAxis.resize.controlledAxis.next.push(id);
                }

                const yAxis = result.find((x: any) => x.id == id);
                if (!yAxis) {
                    continue;
                }

                yAxis.resize.controlledAxis = {
                    next: [],
                    prev: []
                };

                if (i + 1 < addedIndicatorsIdForResizer.length) {
                    const nextId = addedIndicatorsIdForResizer[i + 1];
                    yAxis.resize.controlledAxis.next.push(nextId);
                }
            }
        }

        result.unshift(mainYAxis)
        return result;
    }

    private getMainChartYAxis(): Highcharts.YAxisOptions {
        const me = this;
        const chartStyle = this.tiChartSeries.chartStyleSettings;

        let labelX = 80;
        let resizerEnable = true;
        if (chartStyle?.showSmartRiskLevel) {
            labelX = 112;
        }

        if (this.tiChartSeries.base.marketingWebChartMode) {
            resizerEnable = false;
        }

        if (this.tiChartSeries.base.pnpMode) {
            resizerEnable = false;
            labelX = 55;
        }

        const options: Highcharts.YAxisOptions = {
            id: HighChartBuilder.mainChartAreaName + '-y-axis',
            gridLineColor: 'rgb(220, 220, 220, 0.2)',
            gridLineDashStyle: 'Dash',
            tickColor: chartStyle?.selectedPriceTextColor.toHex() ?? '',
            crosshair: {
                className: 'highchart-main-crosshair y-axis',
                dashStyle: 'Dash',
                snap: false
            },
            labels: {
                y: 4,
                x: labelX,
                style: {
                    color: chartStyle?.selectedPriceTextColor.toHex() ?? ''
                },
                formatter: function () {
                    const val = FormatHelpers.numberToString(parseFloat(this.value + ''));
                    if (me.tiChartSeries.chartStyleSettings?.isComparisonSymbolPercentageMode) {
                        return val + '%';
                    }

                    return '$' + val;
                }
            },
            resize: {
                enabled: resizerEnable
            }
        };

        return options;
    }

    private getSeries(allIndicators: Array<IndicatorOptionModel>): Array<Highcharts.SeriesOptionsType> {
        const mainSeries = this.getMainChartSeries();
        let result = [mainSeries];
        const mainYAxis = HighChartBuilder.mainChartAreaName + '-y-axis';
        for (const indicator of allIndicators) {
            const indicatorConfig = IndicatorHelpers.getIndicatorHighChartConfig(indicator.value);
            if (!indicatorConfig?.series) {
                continue;
            }

            if (indicator.highChartIndicator) {
                const series: any = indicatorConfig.series[0];
                if (indicator.chartAreaName != HighChartBuilder.mainChartAreaName) {
                    series.yAxis = indicator.chartAreaName + '-y-axis';
                    series.lastPrice = {
                        className: 'last-price-indicator-' + indicator.getClassName(),
                        enabled: true
                    };
                }

                if (indicator.value == IndicatorType.HighChartVolume) {
                    const builtDataPoints = TIDataPointToHighChartVolumeDataPointMapper.map(this.tiChartSeries.tiChartPoints);
                    series.data = builtDataPoints.points;
                }

                result.push(series);
            } else {
                const rawIndicatorData = this.tiChartSeries.tiIndicatorsData
                    .find((x: TIIndicatorDataModel) => x.name == indicator.name);
                if (rawIndicatorData) {
                    if (indicator.value == IndicatorType.MACD) {
                        if (indicator.chartAreaName == HighChartBuilder.mainChartAreaName) {
                            continue;
                        }

                        const macdSeries = TIIndicatorDataToMacdSeriesBuilder.map(indicator, rawIndicatorData, indicatorConfig, mainYAxis);
                        result = result.concat(macdSeries);
                    } else if (indicator.value == IndicatorType.DonchianChanel) {
                        const dcSeries = TIIndicatorDataToDCSeriesBuilder.map(indicator, rawIndicatorData, indicatorConfig, mainYAxis);
                        result = result.concat(dcSeries);
                    } else if (indicator.value == IndicatorType.BBands) {
                        let yAxis = '';
                        let useLastPrice = false;

                        if (this.indicatorsToHideFromMainYAxis.some((x: IndicatorType) => x == indicator.value)) {
                            yAxis = rawIndicatorData.chartAreaName + '-ind-y-axis';
                        } else {
                            yAxis = rawIndicatorData.chartAreaName + '-y-axis';

                            if (rawIndicatorData.chartAreaName != HighChartBuilder.mainChartAreaName) {
                                useLastPrice = true;
                            }
                        }

                        const bbandSeries = TIIndicatorDataToBBandsSeriesBuilder.map(indicator, rawIndicatorData, indicatorConfig, useLastPrice, yAxis);
                        result = result.concat(bbandSeries);
                    } else if (indicator.value == IndicatorType.KeltnerChannel) {
                        const keltnerSeries = TIIndicatorDataToKeltnerChannelSeriesBuilder.map(indicator, rawIndicatorData, indicatorConfig, mainYAxis);
                        result = result.concat(keltnerSeries);
                    } else {
                        const indicatorSeries: any = indicatorConfig.series[0];
                        const needZone = rawIndicatorData.needHighChartZone(indicatorSeries.type);
                        const builtDataPoints = TIIndicatorPointToHighChartIndicatorPointMapper.map(rawIndicatorData, indicator.value, needZone);
                        indicatorSeries.name = indicator.name;
                        indicatorSeries.data = builtDataPoints.points;
                        indicatorSeries.className = 'indicator-series ' + rawIndicatorData.getClassName();
                        if (needZone) {
                            rawIndicatorData.zones = builtDataPoints.zones;
                            indicatorSeries.zones = builtDataPoints.zones;
                            indicatorSeries.zoneAxis = 'x';
                        }
                        if (indicator.chartAreaName != HighChartBuilder.mainChartAreaName) {
                            indicatorSeries.yAxis = rawIndicatorData.chartAreaName + '-y-axis';
                            indicatorSeries.lastPrice = {
                                className: 'last-price-indicator-' + indicator.getClassName(),
                                enabled: true
                            };
                        } else {
                            if (this.indicatorsToHideFromMainYAxis.some((x: IndicatorType) => x == indicator.value)) {
                                indicatorSeries.yAxis = rawIndicatorData.chartAreaName + '-ind-y-axis';
                            } else {
                                indicatorSeries.yAxis = rawIndicatorData.chartAreaName + '-y-axis';
                            }

                        }
                        result.push(indicatorSeries);
                    }
                }
            }
        }

        return result;
    }

    private getMainChartSeries(): Highcharts.SeriesOptionsType {
        const result: any = {};
        const me = this;
        const chartStyle = this.tiChartSeries.chartStyleSettings;
        if (!chartStyle) {
            return result;
        }

        const chartType = chartStyle.chartType;

        const dataPoints = TIDataPointToHighChartDataPointMapper.map(me.tiChartSeries);
        result.id = HighChartBuilder.mainChartAreaName;
        result.data = dataPoints.points;
        result.className = this.mainSeriesClassName;
        if (chartType == ChartType.Area) {
            result.type = 'area';
            result.lineWidth = chartStyle.chartTypeLineWidth;
            result.threshold = null;

            if (!chartStyle.isOneColor) {
                result.zones = dataPoints.zones;
                result.zoneAxis = 'x';
            }
        } else if (chartType == ChartType.Line) {
            result.type = 'line';
            result.lineWidth = chartStyle.chartTypeLineWidth;
            if (!chartStyle.isOneColor) {
                result.zones = dataPoints.zones;
                result.zoneAxis = 'x';
            }
        } else if (chartType == ChartType.OHLC) {
            result.type = 'ohlc';
            result.groupPadding = 0;
            result.pointPadding = 0.1;
        } else if (chartType == ChartType.HLC) {
            result.type = 'hlc';
            result.groupPadding = 0;
            result.pointPadding = 0.1;
        } else if (chartType == ChartType.Bar) {
            result.type = 'column';
            result.groupPadding = 0.1;
            result.pointPadding = 0;
            result.threshold = null;
        } else {
            result.type = 'candlestick';
            result.groupPadding = 0.1;
            result.pointPadding = 0.1;
            result.lineWidth = 1;
        }

        if (chartType == ChartType.Line || chartType == ChartType.Area) {
            result.marker = {
                enabled: false,
                states: {
                    hover: {
                        enabled: false
                    }
                }
            };

            result.states = {
                inactive: {
                    opacity: 1
                }
            };
        }
        result.lastPrice = {
            className: 'main-series-last-price',
            enabled: true
        };

        result.zIndex = 99;
        result.dataGrouping = {
            enabled: false
        };
        result.point = {
            events: {
                click: function () {
                    const chart: any = me.tiChartSeries.highStockChart;
                    // console.log(' me.isDrawingToolStart ', me.isDrawingToolStart)
                    // console.log(' me.isDrawingToolRibbonActive ', me.isDrawingToolRibbonActive)
                    // console.log(' me.isDrawingToolRibbonUpdate ', me.isDrawingToolRibbonUpdate)
                    if (!!chart.navigationBindings?.activeAnnotation ||
                        me.isDrawingToolStart ||
                        me.isDrawingToolRibbonActive ||
                        me.isDrawingToolRibbonUpdate ||
                        (me.tiChartSeries.base.isPreview && !window.trace)) {
                        return;
                    }

                    const params: any = this;
                    const toolTip = me.tiChartSeries.base.getComponentsByType(TooltipComponent)[0] as TooltipComponent;
                    toolTip?.renderTooltip(params.x);
                    toolTip?.show();
                },
                mouseOver: function () {
                    me.displayXAxisDateTimeTooltip(this);
                    me.displayYAxisCurrentPrice(true);
                },
                mouseOut: function () {
                    me.hideXAxisDateTimeTooltip();
                    me.hideYAxisCurrentPrice();
                }
            }
        };

        return result;
    }
}
